// frontend/src/components/dashboard/pages/inventory/productionlog/ProductionLog.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  Stack,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Menu,
  Paper
} from '@mui/material';
import {
  TrendingUp,
  Add,
  Edit,
  Delete,
  FileDownload,
  Search,
  FilterList,
  ArrowBack,
  MoreVert,
  Refresh,
  CheckCircle
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { motion } from 'framer-motion';
import { format, startOfMonth, endOfMonth } from 'date-fns';

// Import services and components
import inventoryService from '../services/inventoryService';
import menuService from '../../../services/menuService';
import ProductionForm from './components/ProductionForm';

const MotionDiv = motion.div;

const ProductionLog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const _isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // States
  const [productionRecords, setProductionRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form states
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(startOfMonth(new Date()));
  const [dateTo, setDateTo] = useState(endOfMonth(new Date()));
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Menu and dialog states
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // // Role-based permissions helper
  // const getUserRole = () => {
  //   const userStr = localStorage.getItem('user');
  //   if (userStr) {
  //     try {
  //       const user = JSON.parse(userStr);
  //       return user.role?.toLowerCase() || 'staff';
  //     } catch (error) {
  //       console.error('Error parsing user data:', error);
  //       return 'staff';
  //     }
  //   }
  //   return 'staff';
  // };

  // const userRole = getUserRole();
  // const canEdit = ['admin', 'manager'].includes(userRole);
  // const canDelete = userRole === 'admin';
  // const canExport = ['admin', 'manager'].includes(userRole);
  // console.log('ProductionLog: user:', userRole, 'role:', userRole, 'canEdit:', canEdit, 'canDelete:', canDelete);

   // Role-based permissions: decode token first, fallback to localStorage.user.
   const [userRole, setUserRole] = useState('staff');
 
  const resolveRole = () => {
     // Try decode JWT stored in localStorage.token
     try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const role = payload?.role || payload?.role_name || payload?.roleName || payload?.roleId;
          if (role) return String(role).toLowerCase();
        }
      } catch (err) {
        // Log for debugging and fallback to localStorage.user
        console.warn('ProductionLog.resolveRole: token parse failed, falling back to localStorage.user', err);
      }
 
      // Fallback: parse localStorage.user
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          return (user.role || user.role_name || 'staff').toLowerCase();
        }
      } catch (err) {
        // Log for debugging
        console.warn('ProductionLog.resolveRole: failed to parse localStorage.user', err);
      }
      return 'staff';
    };
 
   useEffect(() => {
     setUserRole(resolveRole());
 
     const onStorage = (e) => {
       if (e.key === 'token' || e.key === 'user') {
         setUserRole(resolveRole());
       }
     };
     window.addEventListener('storage', onStorage);
     return () => window.removeEventListener('storage', onStorage);
   }, []);
 
   const canEdit = ['admin', 'manager'].includes(userRole);
   const canDelete = userRole === 'admin';
   const canExport = ['admin', 'manager'].includes(userRole);
   console.log('ProductionLog: resolved role:', userRole, 'canEdit:', canEdit, 'canDelete:', canDelete);


  // Fetch production records
  const fetchProductionRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 ProductionLog: Fetching production records...');
      
      const response = await inventoryService.getProductionRecords({
        dateFrom: format(dateFrom, 'yyyy-MM-dd'),
        dateTo: format(dateTo, 'yyyy-MM-dd')
      });
      
      if (response && response.success) {
        setProductionRecords(response.data || []);
        console.log('✅ ProductionLog: Records loaded:', response.data?.length || 0);
      } else {
        // Mock data for demonstration
        setProductionRecords([
          {
            production_id: 1,
            date: '2024-01-15',
            product_id: 1,
            product_name: 'Ice Kacang',
            quantity_produced: 50,
            unit_cost: 2.50,
            total_cost: 125.00,
            notes: 'Morning batch preparation',
            created_by: 'John Doe',
            created_at: '2024-01-15T08:30:00Z'
          },
          {
            production_id: 2,
            date: '2024-01-16',
            product_id: 2,
            product_name: 'Mee Goreng',
            quantity_produced: 30,
            unit_cost: 3.80,
            total_cost: 114.00,
            notes: 'Evening preparation for dinner rush',
            created_by: 'Jane Smith',
            created_at: '2024-01-16T16:20:00Z'
          }
        ]);
      }
    } catch (err) {
      console.error('❌ ProductionLog: Error fetching records:', err);
      setError(err.message || 'Failed to load production records');
      setProductionRecords([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  // Fetch products for dropdown
  const fetchProducts = useCallback(async () => {
    try {
      console.log('📦 ProductionLog: Fetching products...');
      
      const response = await menuService.getAllWithCategories();
      setProducts(response || []);
      console.log('✅ ProductionLog: Products loaded:', response?.length || 0);
    } catch (err) {
      console.error('❌ ProductionLog: Error fetching products:', err);
      setProducts([]);
    }
  }, []);

  // Load data on mount and filter changes
  useEffect(() => {
    fetchProductionRecords();
    fetchProducts();
  }, [fetchProductionRecords, fetchProducts]);

  // Filtered records
  const filteredRecords = productionRecords.filter(record => {
    const matchesSearch = record.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProduct = productFilter === 'all' || record.product_id === parseInt(productFilter);
    
    return matchesSearch && matchesProduct;
  });

  // Handle form operations
  const handleAddRecord = () => {
    setEditingRecord(null);
    setFormOpen(true);
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setFormOpen(true);
    setActionAnchorEl(null);
  };

  const handleSaveRecord = async (recordData) => {
    try {
      setFormLoading(true);
      console.log('💾 ProductionLog: Saving record:', recordData);
      
      if (editingRecord) {
        // Update existing record
        await inventoryService.updateProductionRecord(editingRecord.production_id, recordData);
        setSuccess(t('inventory.productionLog.recordUpdated') || 'Production record updated successfully');
      } else {
        // Create new record
        await inventoryService.createProductionRecord(recordData);
        setSuccess(t('inventory.productionLog.recordCreated') || 'Production record created successfully');
      }
      
      setFormOpen(false);
      setEditingRecord(null);
      fetchProductionRecords();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ ProductionLog: Error saving record:', err);
      setError(err.message || 'Failed to save production record');
    } finally {
      setFormLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '0.00' : numValue.toFixed(2);
  };

  const handleDeleteRecord = async () => {
    if (!selectedRecord) return;
    
    try {
      setLoading(true);
      console.log('🗑️ ProductionLog: Deleting record:', selectedRecord.production_id);
      
      await inventoryService.deleteProductionRecord(selectedRecord.production_id);
      
      setSuccess(t('inventory.productionLog.recordDeleted') || 'Production record deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
      fetchProductionRecords();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ ProductionLog: Error deleting record:', err);
      setError(err.message || 'Failed to delete production record');
    } finally {
      setLoading(false);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      console.log('📊 ProductionLog: Exporting records...');
      
      const exportData = filteredRecords.map(record => ({
        Date: format(new Date(record.date), 'yyyy-MM-dd'),
        Product: record.product_name,
        'Quantity Produced': record.quantity_produced,
        'Unit Cost': formatCurrency(record.unit_cost),
        'Total Cost': formatCurrency(record.total_cost),
        Notes: record.notes || '-',
        'Created By': record.created_by,
        'Created At': format(new Date(record.created_at), 'yyyy-MM-dd HH:mm')
      }));
      
      const csvData = convertToCSV(exportData);
      downloadCSV(csvData, `production-log-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      
      setSuccess(t('inventory.exportSuccess') || 'Export completed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ ProductionLog: Export failed:', err);
      setError(t('inventory.exportError') || 'Export failed');
    }
  };

  // CSV utilities
  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => 
      Object.values(row).map(value => 
        typeof value === 'string' && value.includes(',') ? `"${value}"` : value
      ).join(',')
    );
    
    return [headers, ...rows].join('\n');
  };

  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle action menu
  const handleActionClick = (event, record) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedRecord(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setActionAnchorEl(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip title={t('common.back') || 'Back'}>
                <IconButton 
                  onClick={() => navigate('/dashboard/inventory')}
                  sx={{ 
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                <TrendingUp sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {t('inventory.productionLog.title') || 'Production Log'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('inventory.productionLog.subtitle') || 'Track and manage production records'}
                </Typography>
              </Box>
            </Box>
            
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchProductionRecords}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                {t('common.refresh') || 'Refresh'}
              </Button>
              {canExport && (
                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={handleExport}
                  sx={{ borderRadius: 2 }}
                >
                  {t('common.export') || 'Export'}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddRecord}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  }
                }}
              >
                {t('inventory.productionLog.addRecord') || 'Add Record'}
              </Button>
            </Stack>
          </Box>
        </MotionDiv>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Filters */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FilterList />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('inventory.filtersAndSearch') || 'Filters & Search'}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  sx={{ ml: 'auto' }}
                >
                  {filtersExpanded ? 'Less Filters' : 'More Filters'}
                </Button>
              </Box>
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  placeholder={t('inventory.productionLog.searchPlaceholder') || 'Search by product name or notes...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                
                {filtersExpanded && (
                  <>
                    <FormControl sx={{ minWidth: 200 }}>
                      <InputLabel>{t('inventory.product') || 'Product'}</InputLabel>
                      <Select
                        value={productFilter}
                        onChange={(e) => setProductFilter(e.target.value)}
                        label={t('inventory.product') || 'Product'}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="all">{t('inventory.allProducts') || 'All Products'}</MenuItem>
                        {products.map((product) => (
                          <MenuItem key={`product-${product.item_id}`} value={product.item_id}>
                            {product.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <DatePicker
                      label={t('inventory.dateFrom') || 'Date From'}
                      value={dateFrom}
                      onChange={setDateFrom}
                      slotProps={{
                        textField: {
                          sx: { minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                        }
                      }}
                    />
                    
                    <DatePicker
                      label={t('inventory.dateTo') || 'Date To'}
                      value={dateTo}
                      onChange={setDateTo}
                      slotProps={{
                        textField: {
                          sx: { minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                        }
                      }}
                    />
                  </>
                )}
              </Stack>
            </Box>
          </Card>
        </MotionDiv>

        {/* Main Content */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card sx={{ borderRadius: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('inventory.date') || 'Date'}</TableCell>
                      <TableCell>{t('inventory.product') || 'Product'}</TableCell>
                      <TableCell align="right">{t('inventory.quantityProduced') || 'Quantity Produced'}</TableCell>
                      <TableCell align="right">{t('inventory.unitCost') || 'Unit Cost'}</TableCell>
                      <TableCell align="right">{t('inventory.totalCost') || 'Total Cost'}</TableCell>
                      <TableCell>{t('inventory.notes') || 'Notes'}</TableCell>
                      <TableCell>{t('common.createdBy') || 'Created By'}</TableCell>
                      <TableCell>{t('inventory.createdOn') || 'Created On'}</TableCell>

                      <TableCell align="center">{t('inventory.table.actions') || 'Actions'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRecords.map((record, index) => (
                      <TableRow 
                        key={`production-${record.production_id}-${index}`}
                        sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {format(new Date(record.date), 'MMM dd, yyyy')}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {record.product_name}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                            +{record.quantity_produced}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography variant="body2">
                            ${formatCurrency(record.unit_cost)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            ${formatCurrency(record.total_cost)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {record.notes || '-'}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {record.created_by_username || record.created_by || '-'}
                          </Typography>
                          {/* <Typography variant="caption" color="text.secondary">
                            {format(new Date(record.created_at), 'MMM dd, HH:mm')}
                          </Typography> */}
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(record.created_at), 'MMM dd, yyyy HH:mm')}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {/* DEBUG: unconditional icons to verify rendering */}
                            <Tooltip title="Edit (debug)">
                              <IconButton
                                size="small"
                                onClick={() => handleEditRecord(record)}
                                sx={{ bgcolor: 'background.paper', color: 'primary.main' }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="More (debug)">
                              <IconButton
                                size="small"
                                onClick={(e) => handleActionClick(e, record)}
                                sx={{ bgcolor: 'background.paper', color: 'text.primary' }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {/* original conditional logic (keep for reference) */}
                            {/* {canEdit && ( ... )} */}
                            {/* {(canEdit || canDelete) && ( ... )} */}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {filteredRecords.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm || productFilter !== 'all'
                              ? (t('inventory.noRecordsFound') || 'No production records found matching your criteria')
                              : (t('inventory.productionLog.noRecords') || 'No production records found')
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </MotionDiv>

        {/* Action Menu */}
        <Menu
          anchorEl={actionAnchorEl}
          open={Boolean(actionAnchorEl)}
          onClose={handleActionClose}
          PaperProps={{
            sx: { width: 160, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 2 }
          }}
        >
          {canEdit && (
            <MenuItem onClick={() => handleEditRecord(selectedRecord)}>
              <Edit fontSize="small" sx={{ mr: 1.5 }} />
              {t('common.edit') || 'Edit'}
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
              <Delete fontSize="small" sx={{ mr: 1.5 }} />
              {t('common.delete') || 'Delete'}
            </MenuItem>
          )}
        </Menu>

        {/* Production Form Dialog */}
        <ProductionForm
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setEditingRecord(null);
          }}
          onSave={handleSaveRecord}
          initialData={editingRecord}
          products={products}
          loading={formLoading}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white'
          }}>
            <Delete />
            {t('inventory.productionLog.deleteRecord') || 'Delete Production Record'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography>
              {t('inventory.productionLog.deleteConfirmation') || 'Are you sure you want to delete this production record? This action cannot be undone.'}
            </Typography>
            {selectedRecord && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'success.lighter' }}>
                <Typography variant="body2">
                  <strong>Product:</strong> {selectedRecord.product_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Quantity:</strong> {selectedRecord.quantity_produced}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {format(new Date(selectedRecord.date), 'MMM dd, yyyy')}
                </Typography>
              </Paper>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2 }}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={handleDeleteRecord}
              variant="contained"
              color="error"
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              {loading ? (t('common.deleting') || 'Deleting...') : (t('common.delete') || 'Delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ProductionLog;